import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Calendar, ArrowRight, Eye, Search, Camera, ExternalLink } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';

const BlogList = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await axios.get(API_ENDPOINTS.blog);
        setArticles(response.data);
      } catch (err) {
        console.error('Failed to fetch articles:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const categories = ['all', ...new Set(articles.map(a => a.category))];
  const filteredArticles = articles.filter(article => {
    const matchCat = selectedCategory === 'all' || article.category === selectedCategory;
    const matchSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) || article.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchCat && matchSearch;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-navy border-t-transparent mx-auto" />
      </div>
    );
  }

  const featured = filteredArticles[0];
  const rest = filteredArticles.slice(1);

  return (
    <div className="min-h-screen bg-cream">
      <Helmet>
        <title>Actualites - Hope for Peace and Development</title>
        <meta name="description" content="Decouvrez les dernieres actualites de HPD." />
      </Helmet>

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-navy overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="uppercase text-xs md:text-sm tracking-[0.2em] font-bold text-forest mb-4 font-sans">Actualites & Evenements</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-white mb-4 tracking-tight">Nos Actualites</h1>
            <div className="w-16 h-1 bg-forest rounded-full mb-6" />
            <p className="text-lg text-white/70 max-w-3xl font-sans">Restez informes de nos actions, evenements et reussites sur le terrain</p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 bg-white border-b border-black/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex flex-wrap gap-2">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium font-sans transition-all ${
                    selectedCategory === cat ? 'bg-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {cat === 'all' ? 'Toutes' : cat}
                </button>
              ))}
            </div>
            <div className="relative w-full md:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input type="text" placeholder="Rechercher..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 rounded-full" />
            </div>
          </div>
        </div>
      </section>

      {/* Articles */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredArticles.length === 0 ? (
            <div className="text-center py-16"><p className="text-lg text-gray-500 font-sans">Aucune actualite trouvee</p></div>
          ) : (
            <>
              {/* Featured */}
              {featured && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="group cursor-pointer rounded-3xl overflow-hidden border border-black/5 shadow-sm hover:shadow-md transition-all mb-8"
                  onClick={() => {
                    if (featured.external_url) window.open(featured.external_url, '_blank', 'noopener,noreferrer');
                    else navigate(`/actualites/${featured.slug}`);
                  }}
                >
                  <div className="grid md:grid-cols-2 gap-0">
                    <div className="relative h-64 md:h-80 overflow-hidden">
                      <img src={featured.image} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      <div className="absolute top-4 left-4"><Badge className="bg-forest text-white text-xs px-3 py-1">{featured.category}</Badge></div>
                      {featured.external_url && <div className="absolute top-4 right-4 bg-white/90 rounded-full p-1.5"><ExternalLink className="w-4 h-4 text-navy" /></div>}
                    </div>
                    <div className="bg-white p-8 md:p-10 flex flex-col justify-center">
                      <div className="flex items-center text-sm text-gray-500 mb-3 font-sans">
                        <Calendar className="w-4 h-4 mr-2" />{formatDate(featured.created_at)}
                        {featured.external_url && <span className="ml-auto text-xs text-navy font-medium bg-navy/5 px-2 py-0.5 rounded-full">{featured.author}</span>}
                      </div>
                      <h2 className="text-2xl md:text-3xl font-serif font-bold text-navy mb-4 leading-tight group-hover:text-forest transition-colors">{featured.title}</h2>
                      <p className="text-gray-600 text-sm font-sans leading-relaxed mb-6 line-clamp-3">{featured.excerpt}</p>
                      <div className="flex items-center text-forest font-semibold text-sm font-sans group-hover:translate-x-2 transition-transform">
                        <span>{featured.external_url ? 'Lire sur le site source' : 'Lire la suite'}</span>
                        {featured.external_url ? <ExternalLink className="w-4 h-4 ml-2" /> : <ArrowRight className="w-4 h-4 ml-2" />}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {rest.map((article, i) => (
                  <motion.div
                    key={article.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.05 }}
                    className="group cursor-pointer bg-white rounded-2xl overflow-hidden border border-black/5 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300"
                    onClick={() => {
                      if (article.external_url) window.open(article.external_url, '_blank', 'noopener,noreferrer');
                      else navigate(`/actualites/${article.slug}`);
                    }}
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img src={article.image} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute top-3 left-3"><Badge className="bg-forest text-white text-xs px-2.5 py-0.5">{article.category}</Badge></div>
                      {article.external_url && <div className="absolute top-3 right-3 bg-white/90 rounded-full p-1"><ExternalLink className="w-3.5 h-3.5 text-navy" /></div>}
                      {!article.external_url && (
                        <div className="absolute bottom-3 right-3 flex items-center gap-1 text-white text-xs bg-black/30 rounded-full px-2 py-1">
                          <Eye className="w-3 h-3" />{article.views || 0}
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="flex items-center text-xs text-gray-500 mb-2 font-sans">
                        <Calendar className="w-3.5 h-3.5 mr-1.5" />{formatDate(article.created_at)}
                      </div>
                      <h3 className="text-base font-serif font-bold text-navy mb-2 line-clamp-2 group-hover:text-forest transition-colors">{article.title}</h3>
                      <p className="text-gray-500 text-xs font-sans leading-relaxed line-clamp-2">{article.excerpt}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Gallery CTA */}
      <section className="py-20 bg-warmgray">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Camera className="w-10 h-10 text-forest mx-auto mb-4" />
          <h2 className="text-3xl font-serif font-bold text-navy mb-4 tracking-tight">Galerie Photos</h2>
          <p className="text-base text-gray-600 mb-8 font-sans max-w-2xl mx-auto">Retrouvez nos galeries de photos evenementielles</p>
          <button onClick={() => navigate('/galerie')} data-testid="gallery-link-btn" className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-navy text-white font-semibold hover:bg-navy/90 transition-all shadow-lg font-sans">
            <Camera className="w-5 h-5 mr-2" /> Voir la Galerie Photos
          </button>
        </div>
      </section>
    </div>
  );
};

export default BlogList;
